import {
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { juntarClasses } from './classes';
import { alvoDoPortal } from './portal';
import { IconeCheck, IconeMaisAcoes } from './icones';

/**
 * Menu "⋯" de uma linha de lista.
 *
 * ## Por que um menu, e não um botão "Excluir" na linha
 *
 * A lista de 15/09 (item 7) pede exclusão em toda listagem — cliente, assessor,
 * fornecedor, empresa, pessoa, pré-cadastro. Um botão vermelho por linha em
 * 289 clientes é 289 convites ao clique errado; o menu esconde a ação
 * destrutiva atrás de um gesto deliberado e ainda abriga as outras ("Abrir",
 * "Editar") no mesmo lugar.
 *
 * ## Portal, e não `position: absolute`
 *
 * A tabela rola (`overflow-auto`), e um menu absoluto dentro dela seria
 * cortado na última linha. O painel vai para o `body`, ancorado ao botão pela
 * posição na janela, e some ao rolar — reposicionar a cada scroll é mais código
 * do que a leitura "rolou, fechou" merece.
 *
 * ## Teclado e leitor de tela
 *
 * `aria-haspopup="menu"` no botão, `role="menu"` no painel, setas para
 * navegar, Escape e clique fora para fechar. O foco volta ao botão ao fechar.
 *
 * ## Também seletor
 *
 * Com `gatilho`, o botão mostra um texto (o quadro aberto, no Pipeline) em vez
 * do "⋯", e os itens podem vir `marcado` — o escolhido, com o visto — e
 * `separadoAntes`, para a ação ("+ Quadro") não se misturar às opções.
 */

export interface ItemDoMenu {
  readonly rotulo: string;
  readonly aoEscolher: () => void;
  /** Vermelho: exclusão e afins. */
  readonly perigo?: boolean;
  readonly desabilitado?: boolean;
  /** Ajuda curta abaixo do rótulo — "só em simulação", por exemplo. */
  readonly descricao?: string;
  /** Item de escolha: `true` é o escolhido (visto ao lado, `menuitemradio`). */
  readonly marcado?: boolean;
  /** Linha divisória antes do item — a ação depois das opções. */
  readonly separadoAntes?: boolean;
}

export interface PropsMenuDeAcoes {
  readonly itens: readonly ItemDoMenu[];
  /** Nome do que a linha representa — vira o `aria-label` do botão. */
  readonly rotulo: string;
  /** Outro ícone no gatilho — um clipe, quando o menu é só de anexar. */
  readonly icone?: ReactNode;
  /** Prefixo do `aria-label`. "Ações" quando o menu é o de sempre. */
  readonly acao?: string;
  readonly className?: string;
  /**
   * Conteúdo do botão no lugar do ícone — um texto com seta, quando o menu é
   * um seletor. Sem ele, o quadrado com "⋯". O estilo vem de `className`.
   */
  readonly gatilho?: ReactNode;
  /** De que lado do botão o painel se alinha. Padrão: direita. */
  readonly alinhar?: 'esquerda' | 'direita';
  /** Largura do painel, em px. Padrão: 220. */
  readonly largura?: number;
}

export function MenuDeAcoes(props: PropsMenuDeAcoes): JSX.Element {
  const [aberto, setAberto] = useState(false);
  const botao = useRef<HTMLButtonElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const [posicao, setPosicao] = useState<{ top: number; left: number } | null>(null);
  const id = useId();

  useLayoutEffect(() => {
    if (!aberto || botao.current === null) return;
    const r = botao.current.getBoundingClientRect();
    // Alinhado ao botão (direita, por padrão); abre para baixo, ou para cima se não couber.
    const largura = props.largura ?? 220;
    const altura = painel.current?.offsetHeight ?? 0;
    const cabeEmbaixo = r.bottom + altura + 8 <= window.innerHeight;
    const inicio = props.alinhar === 'esquerda' ? r.left : r.right - largura;
    setPosicao({
      top: cabeEmbaixo ? r.bottom + 4 : Math.max(8, r.top - altura - 4),
      left: Math.max(8, Math.min(inicio, window.innerWidth - largura - 8)),
    });
  }, [aberto, props.alinhar, props.largura]);

  useEffect(() => {
    if (!aberto) return;

    const fechar = (): void => {
      setAberto(false);
      botao.current?.focus();
    };
    const aoClicarFora = (ev: MouseEvent): void => {
      const alvo = ev.target as Node;
      if (painel.current?.contains(alvo) || botao.current?.contains(alvo)) return;
      setAberto(false);
    };
    const aoTeclar = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        fechar();
        return;
      }
      if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp') return;
      const itens = Array.from(
        painel.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)') ??
          [],
      );
      if (itens.length === 0) return;
      ev.preventDefault();
      const atual = itens.findIndex((el) => el === document.activeElement);
      const proximo =
        ev.key === 'ArrowDown'
          ? (atual + 1) % itens.length
          : (atual - 1 + itens.length) % itens.length;
      itens[proximo]?.focus();
    };
    const aoRolar = (): void => setAberto(false);

    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    window.addEventListener('scroll', aoRolar, true);
    window.addEventListener('resize', aoRolar);
    // O escolhido (ou o primeiro item) recebe o foco: é o que o padrão de menu pede.
    (
      painel.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]') ??
      painel.current?.querySelector<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)')
    )?.focus();

    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
      window.removeEventListener('scroll', aoRolar, true);
      window.removeEventListener('resize', aoRolar);
    };
  }, [aberto]);

  return (
    <>
      <button
        ref={botao}
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={aberto ? id : undefined}
        aria-label={`${props.acao ?? 'Ações'} — ${props.rotulo}`}
        className={juntarClasses(
          props.gatilho === undefined &&
            'inline-flex h-8 w-8 items-center justify-center rounded-card text-conteudo-secundario transition-colors hover:bg-superficie-vidro hover:text-conteudo-primario focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-acento-ciano',
          props.className,
        )}
        onClick={(ev) => {
          // A linha inteira pode ser clicável (abre o detalhe); o menu não é
          // "abrir".
          ev.stopPropagation();
          setAberto((a) => !a);
        }}
      >
        {props.gatilho ?? props.icone ?? <IconeMaisAcoes />}
      </button>

      {aberto &&
        createPortal(
          <div
            ref={painel}
            id={id}
            role="menu"
            aria-label={`${props.acao ?? 'Ações'} — ${props.rotulo}`}
            style={{
              position: 'fixed',
              top: posicao?.top ?? -9999,
              left: posicao?.left ?? -9999,
              width: props.largura ?? 220,
              zIndex: 60,
            }}
            className="flex flex-col rounded-card border border-borda bg-superficie-elevada p-1 shadow-cartao"
            onClick={(ev) => ev.stopPropagation()}
          >
            {props.itens.map((item) => (
              <Fragment key={item.rotulo}>
                {item.separadoAntes === true && (
                  <div role="separator" className="my-1 border-t border-borda" />
                )}
                <button
                  type="button"
                  role={item.marcado === undefined ? 'menuitem' : 'menuitemradio'}
                  aria-checked={item.marcado}
                  disabled={item.desabilitado}
                  className={juntarClasses(
                    'flex w-full flex-col items-start rounded-card px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-acento-ciano disabled:cursor-not-allowed disabled:opacity-50',
                    item.perigo
                      ? 'text-erro hover:bg-erro/10'
                      : 'text-conteudo-primario hover:bg-superficie-vidro',
                  )}
                  onClick={() => {
                    setAberto(false);
                    item.aoEscolher();
                  }}
                >
                  <span className="flex w-full items-center justify-between gap-2">
                    <span className={item.marcado === true ? 'font-medium' : undefined}>
                      {item.rotulo}
                    </span>
                    {item.marcado === true && (
                      <IconeCheck className="h-4 w-4 shrink-0 text-acento-ciano" />
                    )}
                  </span>
                  {item.descricao !== undefined && (
                    <span className="text-xs font-normal text-conteudo-sutil">
                      {item.descricao}
                    </span>
                  )}
                </button>
              </Fragment>
            ))}
          </div>,
          alvoDoPortal(botao.current),
        )}
    </>
  );
}

/** Célula de tabela com o menu, alinhada à direita e sem encolher. */
export function CelulaDeAcoes(props: { readonly children: ReactNode }): JSX.Element {
  return <div className="flex justify-end">{props.children}</div>;
}
